import { Link } from 'react-router-dom';
import {
  CalendarCheck,
  Phone,
  CheckCircle,
  CaretRight,
  MapPin,
  Buildings,
  Clock,
  ArrowRight,
} from '@phosphor-icons/react';
import { BRAND } from '../lib/brand';
import { SERVICES_INDEX } from '../lib/servicesIndex';
import { findCounty, citiesInCounty } from '../lib/serviceAreas';

/**
 * AreaPageLayout — Used by both county pages and city pages.
 *
 * Props:
 *   kind: 'county' | 'city'
 *   area: county/city object from /lib/serviceAreas.js
 */
export const AreaPageLayout = ({ kind, area }) => {
  const isCounty = kind === 'county';
  const parentCounty = !isCounty ? findCounty(area.countySlug) : null;
  const childCities = isCounty ? citiesInCounty(area.slug) : [];

  return (
    <main data-testid={`area-${kind}-page`}>
      {/* HERO */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${area.heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A0000]/95 via-[#800000]/65 to-[#800000]/40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <nav className="flex items-center gap-2 text-xs text-white/70 mb-6 tracking-wide">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            <CaretRight size={10} weight="bold" />
            <Link to="/areas-we-serve" className="hover:text-[#D4AF37] transition-colors">
              Areas We Serve
            </Link>
            <CaretRight size={10} weight="bold" />
            {parentCounty && (
              <>
                <Link
                  to={`/areas/county/${parentCounty.slug}`}
                  className="hover:text-[#D4AF37] transition-colors"
                >
                  {parentCounty.name}
                </Link>
                <CaretRight size={10} weight="bold" />
              </>
            )}
            <span className="text-[#D4AF37]">{area.name}</span>
          </nav>

          <div className="inline-flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">
              {isCounty ? 'Mobile Notary · County' : 'Mobile Notary · City'}
            </span>
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.05] mb-6 max-w-3xl drop-shadow-2xl">
            Mobile Notary in <span className="italic gold-shimmer">{area.name}</span>
            <span className="block text-2xl sm:text-3xl lg:text-4xl font-light italic text-white/90 mt-3">
              Tennessee
            </span>
          </h1>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl leading-relaxed mb-8 drop-shadow">
            {isCounty
              ? `Trusted mobile notary, signing-agent, and apostille service across ${area.name} — ${area.population} residents, ${area.cityCount}+ cities, one MBA-led notary serving them all.`
              : `MBA-led mobile notary covering every neighborhood and ZIP code in ${area.name}, Tennessee. Same-day appointments, evenings and weekends — India comes to you.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/booking"
              data-testid="area-book-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-7 py-3.5 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase shadow-2xl"
            >
              <CalendarCheck weight="bold" size={18} />
              Book a {area.name} Notary
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-3 border border-white/60 backdrop-blur-sm bg-white/10 text-white hover:bg-white hover:text-[#800000] transition-all px-7 py-3.5 rounded-sm font-semibold text-sm"
            >
              <Phone weight="bold" size={18} />
              {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* INTRO + facts panel */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Left — long-form intro */}
            <div className="lg:col-span-7">
              <p className="flex items-center gap-3 text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-5">
                <span className="h-px w-8 bg-[#D4AF37]" />
                {isCounty ? `Serving All of ${area.name}` : `Mobile Notary in ${area.name}, TN`}
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#800000] mb-6 leading-tight">
                A traveling notary {area.name} families and businesses{' '}
                <span className="italic">can rely on.</span>
              </h2>
              <p className="text-[#1A1A1A] text-base leading-[1.85] mb-6 max-w-2xl">
                {area.intro}
              </p>
              <p className="text-[#1A1A1A] text-base leading-[1.85] max-w-2xl">
                Bonded, commissioned, and MBA-trained — India brings the same precision she
                learned in corporate development to every {area.name}{' '}
                {isCounty ? 'county' : 'city'} appointment. Loan signings, apostille,
                estate planning, fingerprinting, I-9 verification, and wedding officiant
                services all delivered with same-day availability and 24/7 emergency
                response.
              </p>
            </div>

            {/* Right — sticky facts card */}
            <aside className="lg:col-span-5">
              <div className="bg-[#FBF8F1] border border-[#E8E2D5] rounded-sm p-7 sticky top-28">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#800000] font-bold mb-4">
                  Quick Facts
                </p>
                <dl className="space-y-4 text-sm">
                  {isCounty && (
                    <>
                      <div>
                        <dt className="text-[#5C5C5C] text-xs uppercase tracking-wide mb-0.5">County Seat</dt>
                        <dd className="text-[#1A1A1A] font-medium">{area.seat}</dd>
                      </div>
                      <div>
                        <dt className="text-[#5C5C5C] text-xs uppercase tracking-wide mb-0.5">Population</dt>
                        <dd className="text-[#1A1A1A] font-medium">{area.population}</dd>
                      </div>
                    </>
                  )}
                  {!isCounty && (
                    <>
                      <div>
                        <dt className="text-[#5C5C5C] text-xs uppercase tracking-wide mb-0.5">County</dt>
                        <dd className="text-[#1A1A1A] font-medium">
                          {parentCounty ? (
                            <Link
                              to={`/areas/county/${parentCounty.slug}`}
                              className="text-[#800000] hover:text-[#D4AF37] underline-offset-2 hover:underline"
                            >
                              {parentCounty.name}
                            </Link>
                          ) : (
                            area.countyName
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[#5C5C5C] text-xs uppercase tracking-wide mb-0.5">ZIP Codes Served</dt>
                        <dd className="text-[#1A1A1A] font-medium text-xs leading-relaxed">{area.zips}</dd>
                      </div>
                    </>
                  )}
                  <div>
                    <dt className="text-[#5C5C5C] text-xs uppercase tracking-wide mb-0.5">Local Landmarks & Hospitals</dt>
                    <dd className="text-[#1A1A1A] text-xs leading-relaxed">{area.landmarks}</dd>
                  </div>
                  {isCounty && area.courthouses && (
                    <div>
                      <dt className="text-[#5C5C5C] text-xs uppercase tracking-wide mb-0.5">Courthouses</dt>
                      <dd className="text-[#1A1A1A] text-xs leading-relaxed">{area.courthouses}</dd>
                    </div>
                  )}
                  <div className="pt-4 border-t border-[#E8E2D5] flex items-center gap-2 text-[#800000]">
                    <Clock weight="fill" size={16} />
                    <span className="text-xs font-semibold uppercase tracking-wide">24 / 7 · Same-Day</span>
                  </div>
                </dl>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* WHAT WE DO HERE */}
      <section className="py-20 bg-[#FBF8F1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              In {area.name}
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
              What India handles <span className="italic">{isCounty ? 'across the county' : 'in your neighborhood'}.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {area.bullets.map((b, i) => (
              <div
                key={i}
                data-testid={`area-bullet-${i}`}
                className="bg-white border border-[#E8E2D5] hover:border-[#D4AF37]/50 hover:shadow-xl rounded-sm p-6 transition-all"
              >
                <CheckCircle weight="fill" size={22} className="text-[#D4AF37] mb-3" />
                <p className="text-[#1A1A1A] text-sm leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES IN COUNTY (county pages only) */}
      {isCounty && childCities.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
                Cities India Serves in {area.name}
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
                {area.name} cities & <span className="italic">neighborhoods.</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {childCities.map((city) => (
                <Link
                  key={city.slug}
                  to={`/areas/city/${city.slug}`}
                  data-testid={`county-child-city-${city.slug}`}
                  className="group bg-[#FBF8F1] border border-[#E8E2D5] hover:border-[#800000]/30 hover:shadow-xl rounded-sm p-6 transition-all block"
                >
                  <div className="flex items-start gap-3 mb-2">
                    <Buildings weight="duotone" size={24} className="text-[#800000] flex-shrink-0 mt-0.5" />
                    <h3 className="font-serif-display text-xl font-semibold text-[#800000] leading-tight group-hover:text-[#5C0000]">
                      {city.name}
                    </h3>
                  </div>
                  <p className="text-[#5C5C5C] text-xs uppercase tracking-wide mb-3">
                    ZIP {city.zips.split(',')[0].trim()}
                    {city.zips.includes(',') ? ' & more' : ''}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#800000] group-hover:text-[#D4AF37]">
                    Visit Page <ArrowRight weight="bold" size={12} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>

            {/* Additional neighborhoods — display-only pills */}
            {area.neighborhoods && area.neighborhoods.length > 0 && (
              <div className="mt-14 pt-10 border-t border-[#E8E2D5]">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-bold text-center mb-6">
                  Also serving these {area.name} communities
                </p>
                <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
                  {area.neighborhoods.map((n) => (
                    <span
                      key={n}
                      data-testid={`county-neighborhood-${n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#FBF8F1] border border-[#E8E2D5] text-[#1A0000] text-sm rounded-sm"
                    >
                      <MapPin weight="fill" size={12} className="text-[#D4AF37]" />
                      {n}
                    </span>
                  ))}
                </div>
                <p className="text-center text-xs text-[#5C5C5C] mt-6 italic">
                  Don't see your neighborhood? India still serves it — just call.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* NEARBY CITIES (city pages only) */}
      {!isCounty && area.nearbyCities && area.nearbyCities.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
                Nearby Cities
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
                Also serving <span className="italic">your neighbors.</span>
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {area.nearbyCities.map((slug) => {
                const friendly = slug
                  .split('-')
                  .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                  .join(' ');
                return (
                  <Link
                    key={slug}
                    to={`/areas/city/${slug}`}
                    data-testid={`nearby-city-${slug}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FBF8F1] border border-[#E8E2D5] hover:border-[#800000]/40 hover:bg-white text-[#800000] text-sm font-medium rounded-sm transition-all"
                  >
                    <MapPin weight="fill" size={14} className="text-[#D4AF37]" />
                    {friendly}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SERVICES OFFERED HERE */}
      <section className="py-20 bg-[#FBF8F1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              Services Available in {area.name}
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
              Every notarial act, <span className="italic">delivered to {area.name}.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES_INDEX.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`area-service-link-${s.slug}`}
                className="group bg-white border border-[#E8E2D5] hover:border-[#800000]/30 hover:shadow-lg p-5 rounded-sm transition-all flex items-center justify-between gap-3"
              >
                <span className="text-[#1A1A1A] text-sm font-medium group-hover:text-[#800000]">
                  {s.short}
                </span>
                <ArrowRight weight="bold" size={14} className="text-[#D4AF37] group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-[#800000] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <MapPin weight="duotone" size={48} className="text-[#D4AF37] mx-auto mb-5" />
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-5 leading-tight">
            Need a notary in {area.name} <span className="italic gold-shimmer">today?</span>
          </h2>
          <p className="text-base sm:text-lg text-white/85 mb-9 max-w-2xl mx-auto leading-relaxed">
            India answers the phone. Most {area.name} appointments scheduled within 60 minutes — including evenings, weekends, and emergencies.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/booking"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-7 py-4 rounded-sm font-semibold text-sm uppercase tracking-[0.15em]"
            >
              <CalendarCheck weight="bold" size={18} />
              Book Now
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#800000] transition-all px-7 py-4 rounded-sm font-semibold text-sm"
            >
              <Phone weight="bold" size={18} />
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
